import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import { getIntlTranslation } from '@/utils/intl';
import { TranslationOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React, { useEffect, useMemo, useRef } from 'react';
import { useIntl, useSelector } from 'umi';
import { VariableOption } from './variableOptions';

import styles from './VariableOptionList.less';

interface VariableOptionListProps {
  options: VariableOption[];
  onPick: (option: VariableOption) => void;
  /**
   * The row the arrow keys are on, for the inline autocomplete. Omitted, nothing
   * is highlighted — the picker button drives its list with the mouse.
   */
  activeIndex?: number;
  /** Rendered when nothing matches; omitted, an empty list renders nothing. */
  empty?: React.ReactNode;
}

/**
 * The shared list of things a field can hold instead of typed words: a group header
 * wherever the group changes, then the friendly name over the exact Twig a pick
 * inserts. Showing that line is the point — it is what the subject line will read
 * afterwards.
 *
 * A saved text is marked with an icon rather than a word: its primary line is the
 * wording itself, which is indistinguishable from a variable's label until you read
 * the line below it.
 */
const VariableOptionList: React.FC<VariableOptionListProps> = ({
  options,
  onPick,
  activeIndex,
  empty,
}) => {
  const intl = useIntl();
  const activeRef = useRef<HTMLButtonElement>(null);
  const descriptions = useSelector(
    EmailCenterSelectors.selectVariableDescriptions,
  );
  // The theme groups are added by the Email Center, so the server has no text for them.
  const themeDescriptions = useMemo<Record<string, string>>(
    () => ({
      'theme.colors': getIntlTranslation(intl, {
        id: 'pages.emailCenter.variableInsert.themeColor',
        defaultMessage: 'A colour from the journey theme.',
      }),
      'theme.assets': getIntlTranslation(intl, {
        id: 'pages.emailCenter.variableInsert.themeAsset',
        defaultMessage: 'An image or file from the journey theme.',
      }),
    }),
    [intl],
  );

  // Arrowing past the fold has to bring the row with it; the list scrolls.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  return (
    <div
      className={styles.list}
      // Keeps the caret in the field: a blur would close the autocomplete
      // before the click on this row ever lands.
      onMouseDown={(event) => event.preventDefault()}
    >
      {options.map((option, index) => {
        const description =
          option.kind === 'variable'
            ? descriptions?.[option.path] ??
              themeDescriptions[option.namespace]
            : undefined;
        return (
        <React.Fragment key={`${option.kind}:${option.path}`}>
          {/* The catalog is already ordered by group, so a header belongs
              wherever it changes — grouping without a second data structure. */}
          {option.groupKey !== options[index - 1]?.groupKey && (
            <div className={styles.groupHeader}>{option.groupLabel}</div>
          )}
          {/* The arrow-key row shows its text without a hover; undefined leaves the rest to the mouse. */}
          <Tooltip
            title={description}
            open={description && index === activeIndex ? true : undefined}
            placement="right"
            mouseEnterDelay={0.3}
          >
            <button
              type="button"
              ref={index === activeIndex ? activeRef : undefined}
              className={styles.item}
              data-active={index === activeIndex ? '1' : undefined}
              onClick={() => onPick(option)}
            >
              <span className={styles.itemLabel}>
                {option.kind === 'translation' && (
                  <TranslationOutlined className={styles.itemIcon} />
                )}
                <span className={styles.itemText}>{option.label}</span>
              </span>
              <code className={styles.itemPath}>{option.snippet}</code>
            </button>
          </Tooltip>
        </React.Fragment>
        );
      })}

      {options.length === 0 && empty && (
        <span className={styles.empty}>{empty}</span>
      )}
    </div>
  );
};

export default React.memo(VariableOptionList);
